import { DataTable, type DataTableColumn, type DataTableProps, type DataTableSortStatus } from 'mantine-datatable';
import { useEffect, useMemo, useState } from 'react';
import { EmptyState } from './kit';
import { tr, uiLocale } from '../lib/i18n';

export type { DataTableColumn, DataTableSortStatus };

/** Shared look for every table: compact rows, quiet header, Arabic texts. */
export const tableDefaults = {
  withTableBorder: false,
  borderRadius: 0,
  highlightOnHover: true,
  verticalSpacing: 'xs',
  horizontalSpacing: 'md',
  minHeight: 200,
  fz: 'sm',
  noRecordsText: tr('لا توجد بيانات', 'No records'),
  recordsPerPageLabel: tr('في الصفحة', 'Per page'),
  paginationText: ({ from, to, totalRecords }: { from: number; to: number; totalRecords: number }) =>
    tr(`${from}–${to} من ${totalRecords.toLocaleString(uiLocale())}`, `${from}–${to} of ${totalRecords.toLocaleString(uiLocale())}`),
  paginationActiveBackgroundColor: 'ink.9',
  emptyState: <EmptyState title={tr('لا توجد بيانات', 'Nothing here yet')} text={tr('جرّب تغيير البحث أو الفلاتر.', 'Try another search or fewer filters.')} />,
} as const;

type SortValue = string | number | boolean | null | undefined | Date;
export type Column<T> = DataTableColumn<T> & { sortValue?: (r: T) => SortValue };

const collator = new Intl.Collator(['ar', 'en'], { numeric: true, sensitivity: 'base' });
function compare(a: SortValue, b: SortValue) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (a instanceof Date || b instanceof Date) return +new Date(a as Date) - +new Date(b as Date);
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return collator.compare(String(a), String(b));
}

/**
 * A table over records already in memory: sorting, paging (20 / 50 / 100) and the shared look.
 * Pages pass the filtered list; the page resets when it changes.
 */
export function ClientTable<T extends object>({
  records,
  columns,
  pageSize = 20,
  defaultSort,
  idAccessor,
  ...rest
}: {
  records: T[];
  columns: Column<T>[];
  pageSize?: number;
  defaultSort?: DataTableSortStatus<T>;
  idAccessor?: DataTableProps<T>['idAccessor'];
} & Omit<DataTableProps<T>, 'records' | 'columns' | 'page' | 'onPageChange' | 'totalRecords' | 'recordsPerPage' | 'sortStatus' | 'onSortStatusChange' | 'idAccessor' | 'groups' | 'customLoaderColor'>) {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(pageSize);
  const [sort, setSort] = useState<DataTableSortStatus<T> | undefined>(defaultSort);
  useEffect(() => setPage(1), [records, size]);

  const sorted = useMemo(() => {
    if (!sort) return records;
    const col = columns.find((c) => c.accessor === sort.columnAccessor);
    const get = col?.sortValue ?? ((r: T) => (r as Record<string, unknown>)[String(sort.columnAccessor)] as SortValue);
    const out = [...records].sort((a, b) => compare(get(a), get(b)));
    return sort.direction === 'desc' ? out.reverse() : out;
  }, [records, sort, columns]);
  const slice = sorted.slice((page - 1) * size, page * size);
  const paged = records.length > size;

  const props = {
    ...tableDefaults,
    idAccessor,
    records: slice,
    columns,
    sortStatus: sort,
    onSortStatusChange: setSort,
    ...(paged ? { page, onPageChange: setPage, totalRecords: records.length, recordsPerPage: size, recordsPerPageOptions: [20, 50, 100], onRecordsPerPageChange: setSize } : {}),
    ...rest,
  };
  // mantine-datatable's props are a union (paged / not paged); the shape above is one of them
  return <DataTable<T> {...(props as unknown as DataTableProps<T>)} />;
}

export { DataTable };
